/**
 * Sonda de diagnostico del marco. TEMPORAL.
 *
 * Se enciende con `?diag=1` en la direccion y no se dibuja de ninguna otra
 * forma, asi que no le estorba a nadie en la tienda.
 *
 * Existe porque el defecto —la barra inferior que queda subida con una franja
 * muerta debajo— NO se reproduce en el navegador de escritorio: es de iOS. Sin
 * numeros del aparato que falla, cualquier arreglo es una apuesta, y ya se
 * gastaron dos. Esto imprime en pantalla lo que mide el telefono para poder
 * leerlo en una captura.
 *
 * Cuando el caso quede cerrado, este archivo se borra junto con su render.
 */
import { useEffect, useState } from 'react';

interface Medidas {
  pantalla: string;
  innerH: number;
  clientH: number;
  vvH: number;
  vvTop: number;
  vvScale: number;
  appVh: string;
  vh: number;
  dvh: number;
  marcoH: number;
  navBottom: number;
  hueco: number;
  huecoVv: number;
  huecoIcb: number;
  safeT: string;
  safeB: string;
  mq: boolean;
  navStandalone: boolean;
  marcado: boolean;
}

/**
 * Sonda del borde de abajo: un pixel anclado con `position: fixed; bottom: 0`.
 *
 * Existe porque el hueco se media contra `window.innerHeight`, que es UNO DE LOS
 * NUMEROS SOSPECHOSOS. Si iOS reporta la ventana corta, el hueco calculado asi
 * sale 0 y la sonda declara "ok" mientras el dueño esta viendo la franja muerta
 * en su telefono. Este elemento no calcula: se deja poner donde el navegador
 * cree que termina el viewport, y su `bottom` es esa respuesta.
 */
let sonda: HTMLElement | null = null;

function borde(): number {
  if (!sonda) {
    sonda = document.createElement('div');
    sonda.style.cssText =
      'position:fixed;left:0;bottom:0;width:1px;height:1px;pointer-events:none;opacity:0';
    document.body.appendChild(sonda);
  }
  return sonda.getBoundingClientRect().bottom;
}

/**
 * Cuanto miden DE VERDAD `100vh` y `100dvh` en este aparato.
 *
 * Es la comprobacion que decide el caso, y no se puede deducir leyendo: en la
 * PWA de iOS las dos unidades NO valen lo mismo —`dvh` no queda inicializada en
 * el arranque en frio y `vh` si—, mientras que en el navegador coinciden. Con
 * los dos numeros al lado de `screen`, la captura del telefono dice sola cual
 * de las dos esta corta y por cuanto.
 */
let reglas: { vh: HTMLElement; dvh: HTMLElement } | null = null;

function unidades(): { vh: number; dvh: number } {
  if (!reglas) {
    const hacer = (alto: string): HTMLElement => {
      const el = document.createElement('div');
      el.style.cssText = `position:fixed;left:0;top:0;width:1px;height:${alto};pointer-events:none;opacity:0`;
      document.body.appendChild(el);
      return el;
    };
    reglas = { vh: hacer('100vh'), dvh: hacer('100dvh') };
  }
  return {
    vh: Math.round(reglas.vh.getBoundingClientRect().height),
    dvh: Math.round(reglas.dvh.getBoundingClientRect().height),
  };
}

function medir(): Medidas {
  const d = document.documentElement;
  const cs = getComputedStyle(d);
  const vv = window.visualViewport;
  const marco = document.querySelector('.h-app');
  const nav = [...document.querySelectorAll('nav')].find((n) =>
    n.className.includes('lg:hidden'),
  );
  const nr = nav?.getBoundingClientRect();
  return {
    pantalla: `${window.screen.width}x${window.screen.height}`,
    innerH: window.innerHeight,
    clientH: d.clientHeight,
    vvH: Math.round(vv?.height ?? -1),
    vvTop: Math.round(vv?.offsetTop ?? -1),
    vvScale: Number((vv?.scale ?? -1).toFixed(2)),
    appVh: cs.getPropertyValue('--app-vh').trim() || '(sin definir)',
    vh: unidades().vh,
    dvh: unidades().dvh,
    marcoH: Math.round(marco?.getBoundingClientRect().height ?? -1),
    navBottom: nr ? Math.round(nr.bottom) : -1,
    // TRES huecos, contra las tres respuestas posibles de "donde termina la
    // pantalla". Si discrepan, la que discrepa es la mentira, y ahi esta el
    // defecto: un solo numero no lo puede decir.
    hueco: nr ? Math.round(window.innerHeight - nr.bottom) : -1,
    huecoVv: nr && vv ? Math.round(vv.height - nr.bottom) : -1,
    huecoIcb: nr ? Math.round(borde() - nr.bottom) : -1,
    safeT: cs.getPropertyValue('--safe-t').trim(),
    safeB: cs.getPropertyValue('--safe-b').trim(),
    // LAS TRES SEÑALES POR SEPARADO. Es lo que distingue "no estoy instalado"
    // de "estoy instalado y la consulta de medios no se entera", que era el
    // defecto: en iOS `display-mode` no es de fiar y `navigator.standalone` si.
    mq: window.matchMedia('(display-mode: standalone)').matches,
    navStandalone: (window.navigator as { standalone?: boolean }).standalone === true,
    marcado: d.hasAttribute('data-standalone'),
  };
}

export function ViewportDiag(): React.ReactNode {
  const [m, setM] = useState<Medidas | null>(null);

  useEffect(() => {
    const actualizar = (): void => setM(medir());
    actualizar();
    const t = window.setInterval(actualizar, 500);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', actualizar);
    vv?.addEventListener('scroll', actualizar);
    window.addEventListener('resize', actualizar);
    return () => {
      window.clearInterval(t);
      vv?.removeEventListener('resize', actualizar);
      vv?.removeEventListener('scroll', actualizar);
      window.removeEventListener('resize', actualizar);
    };
  }, []);

  if (!m) return null;

  // El hueco es el sintoma: si vale 0 el marco llega al borde de la pantalla.
  // Se juzga por el PEOR de los tres, no por `innerHeight`: basta con que una
  // de las tres medidas del viewport diga que sobra pantalla para que en el
  // aparato se vea la franja.
  const malo = Math.max(m.hueco, m.huecoVv, m.huecoIcb) > 2;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        background: malo ? 'rgba(190,20,20,0.96)' : 'rgba(10,90,40,0.96)',
        color: '#fff',
        font: '600 12px/1.35 ui-monospace, SFMono-Regular, Menlo, monospace',
        padding: '6px 8px',
        pointerEvents: 'none',
        whiteSpace: 'pre-wrap',
      }}
    >
      {[
        `HUECO inner ${m.hueco}  vv ${m.huecoVv}  icb ${m.huecoIcb}   ${malo ? '<-- FALLA' : 'ok'}`,
        `pantalla ${m.pantalla}`,
        `INSTALADA: mq ${m.mq ? 'si' : 'NO'}  nav ${m.navStandalone ? 'si' : 'NO'}  marca ${m.marcado ? 'SI' : 'no'}`,
        `innerH ${m.innerH}  clientH ${m.clientH}`,
        `vv h ${m.vvH} top ${m.vvTop} scale ${m.vvScale}`,
        `--app-vh ${m.appVh}   marco ${m.marcoH}`,
        `100vh ${m.vh}  100dvh ${m.dvh}${m.vh !== m.dvh ? '  <-- DISCREPAN' : ''}`,
        `navBottom ${m.navBottom}  safe t ${m.safeT} b ${m.safeB}`,
      ].join('\n')}
    </div>
  );
}
